import { ThreeEvent, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  type MeasurementPlane,
  type PlaneVec2,
  deletePlaneVertex,
  insertPlaneVertex,
  pushPlaneHistory,
  updatePlane,
} from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { frameToWorld, projectToFrame } from '../../../../utils/measurementPlane/frame.ts';
import { RENDER_LIFT_M } from '../../../../utils/measurementPlane/geometry.ts';

const HANDLE = '#ffffff'; // vertex handle (white) with a dark rim
const RIM = '#1a1a1a';
const ADD = '#b3ec49'; // midpoint "add a vertex" handle (lime)

/** Editable vertex handles for the selected surface: drag a white handle to move a vertex,
 *  click a small green midpoint to add one, right-click a vertex to delete it. Orbit is
 *  suspended during a drag and one history snapshot is pushed per drag (single undo step). */
export function MeasurementPlaneVertexEditor(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const drawActive = useAppSelector((s) => s.measurementPlanes.draw.active);
  const exclusionActive = useAppSelector((s) => s.measurementPlanes.exclusionDraw.active);
  // Vertex handles are only for editing the surface — hide them in "All model points".
  const pointsView = useAppSelector((s) => s.measurementPlanes.pointsView);
  const plane = useAppSelector((s) => s.measurementPlanes.planes.find((p) => p.id === s.measurementPlanes.selectedId));
  const { camera, gl, controls } = useThree() as unknown as {
    camera: THREE.Camera;
    gl: THREE.WebGLRenderer;
    controls?: { enabled: boolean } | null;
  };

  if (!plane || !plane.visible || drawActive || exclusionActive || pointsView !== 'surface' || plane.polygon.length < 3)
    return null;

  const vertWorld = (p: MeasurementPlane, v: PlaneVec2): [number, number, number] => {
    if (p.frame) {
      const w = frameToWorld(v, p.frame);
      return [
        w[0] + p.frame.normal[0] * RENDER_LIFT_M,
        w[1] + p.frame.normal[1] * RENDER_LIFT_M,
        w[2] + p.frame.normal[2] * RENDER_LIFT_M,
      ];
    }
    return [v[0], v[1], p.height + RENDER_LIFT_M];
  };

  const startDrag = (index: number) => (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    const startPolygon = plane.polygon.map((p) => [...p] as PlaneVec2);
    dispatch(pushPlaneHistory());
    if (controls) controls.enabled = false;

    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    const planeMath = plane.frame
      ? new THREE.Plane().setFromNormalAndCoplanarPoint(
          new THREE.Vector3(plane.frame.normal[0], plane.frame.normal[1], plane.frame.normal[2]),
          new THREE.Vector3(plane.frame.origin[0], plane.frame.origin[1], plane.frame.origin[2]),
        )
      : new THREE.Plane(new THREE.Vector3(0, 0, 1), -plane.height);

    const onMove = (ev: PointerEvent) => {
      const r = gl.domElement.getBoundingClientRect();
      ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      if (!raycaster.ray.intersectPlane(planeMath, hit)) return;
      const coord: PlaneVec2 = plane.frame ? projectToFrame([hit.x, hit.y, hit.z], plane.frame) : [hit.x, hit.y];
      const next = startPolygon.slice();
      next[index] = coord;
      dispatch(updatePlane({ id: plane.id, changes: { polygon: next } }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (controls) controls.enabled = true;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const n = plane.polygon.length;

  return (
    <group>
      {/* Vertex handles — white sphere with a dark rim; drag to move, right-click to delete. */}
      {plane.polygon.map((v, i) => (
        <group key={`v${i}`} position={vertWorld(plane, v)}>
          <mesh renderOrder={999} raycast={() => null}>
            <sphereGeometry args={[0.28, 16, 16]} />
            <meshBasicMaterial color={RIM} depthTest={false} />
          </mesh>
          <mesh
            renderOrder={1000}
            onPointerDown={startDrag(i)}
            onContextMenu={(e: ThreeEvent<MouseEvent>) => {
              e.stopPropagation();
              e.nativeEvent.preventDefault();
              dispatch(deletePlaneVertex({ id: plane.id, index: i }));
            }}
          >
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshBasicMaterial color={HANDLE} depthTest={false} />
          </mesh>
        </group>
      ))}

      {/* Midpoint "add a vertex" handles — click to insert a vertex on that edge. */}
      {plane.polygon.map((v, i) => {
        const next = plane.polygon[(i + 1) % n];
        const mid: PlaneVec2 = [(v[0] + next[0]) / 2, (v[1] + next[1]) / 2];
        return (
          <mesh
            key={`m${i}`}
            position={vertWorld(plane, mid)}
            renderOrder={1001}
            onClick={(e: ThreeEvent<MouseEvent>) => {
              e.stopPropagation();
              dispatch(insertPlaneVertex({ id: plane.id, index: i + 1, xy: mid }));
            }}
          >
            <sphereGeometry args={[0.13, 14, 14]} />
            <meshBasicMaterial color={ADD} transparent opacity={0.85} depthTest={false} />
          </mesh>
        );
      })}
    </group>
  );
}
